/**
 * The shell: start gate → scene selector → one scene at a time → an input.
 *
 * One screen, mobile first. The gate is honest about what it does: it creates
 * an AudioContext inside a user gesture, because iOS Safari requires it, and it
 * asks for the camera in that same gesture, because Safari also requires
 * video.play() to happen inside one.
 *
 * Input is either the camera or the on-screen pad. They are interchangeable by
 * construction — both are an InputSource emitting the same FeatureFrame — so
 * the pad remains the way to play with no camera, no permission and no model
 * download, and the way the mapping layer is exercised in tests.
 */

import './style.css';
import { getBus, isStarted, outputLatencyMs, startAudio } from './audio/engine';
import {
  createRecorder, deliverTake, downloadTake, shareTakeNow,
  type Recorder, type Take,
} from './audio/recorder';
import { createTransport, type Transport } from './audio/transport';
import { createControlLog } from './capture/controllog';
import { getExperiments, setExperiments } from './mapping/config';
import { createControlPipeline } from './mapping/pipeline';
import type { FeatureFrame, SceneId } from './mapping/types';
import { SCENE_FACTORIES, SCENE_LABELS, SCENE_ORDER, type Scene } from './scenes';
import { button, el } from './ui/controls';
import { faviconDataUrl, markSvg } from './ui/mark';
import { createGesturePad } from './ui/pad';
import { openCalibration, loadStoredCalibration } from './ui/calibrate';
import { parseCalibration } from './mapping/calibration';
import { createPreview, type Preview } from './ui/preview';
import { createCameraSource, type CameraSource } from './vision/camera';

const app = document.getElementById('app')!;
const pad = createGesturePad('xy');
/**
 * The one vocabulary instance. Every frame from whichever source is active
 * passes through here; scenes receive the resulting ControlState and never
 * see a raw input event. One memory, one implementation of every threshold.
 */
const pipeline = createControlPipeline();
/** Runs exactly while the recorder runs; delivered as a .jsonl beside the take. */
const controlLog = createControlLog();
let transport: Transport | null = null;
let recorder: Recorder | null = null;
let scene: Scene | null = null;
let sceneId: SceneId = 'bass';
let camera: CameraSource | null = null;
let preview: Preview | null = null;
/** Which source drives the scene. 'pad' from the start in a no-camera session. */
let activeInput: 'camera' | 'pad' = 'pad';
let inputPick: { camera: HTMLButtonElement; pad: HTMLButtonElement } | null = null;
let padOverlay: HTMLElement | null = null;

/** One place mirrors the input state into every affordance that shows it. */
function reflectInput(): void {
  inputPick?.camera.setAttribute('aria-checked', String(activeInput === 'camera'));
  inputPick?.pad.setAttribute('aria-checked', String(activeInput === 'pad'));
  // The overlay exists to explain a DEAD pad; a live pad needs no explaining.
  if (padOverlay) padOverlay.hidden = activeInput === 'pad';
}

async function chooseInput(next: 'camera' | 'pad'): Promise<void> {
  if (next === activeInput) return;
  await setInput(next);
  reflectInput();
}

const stage = el('div', { class: 'stage', id: 'stage' });
const statusEl = el('p', { class: 'status', role: 'status', 'aria-live': 'polite' }, ['—']);
/** The dropout contract requires the latched state to be VISIBLE (gesture-vocabulary.md). */
const latchEl = el('p', { class: 'status warn', role: 'status', hidden: 'true' },
  ['pitch hand lost — degree held at its last value']);
const readoutEl = el('dl', { class: 'readout', 'aria-label': 'Live values' });
const meterBar = el('i', { class: 'level-fill' });
const tabsEl = el('nav', { class: 'tabs', role: 'tablist', 'aria-label': 'Scene' });
/** Holds the deferred Share/Download buttons for a finished take. */
const shareSlot = el('div', { class: 'row share-slot' });

function setStatus(msg: string): void { statusEl.textContent = msg; }

/**
 * Camera status outlives the start gate. The camera reports through this for
 * its whole life — "camera stalled", "released by the system", recovery — but
 * the gate's note element is removed at boot. Writing into a detached node
 * made every post-boot message invisible, which silenced the stall detector's
 * entire user-facing half. Route to the gate note while it exists, then to the
 * live status line and the preview bar, where the performer is actually looking.
 */
let gateNote: HTMLElement | null = null;
function cameraStatus(msg: string): void {
  if (gateNote?.isConnected) { gateNote.textContent = msg; return; }
  setStatus(msg);
  preview?.setNote(msg);
}

/**
 * Calibration entry: restore silently when a stored calibration exists,
 * otherwise run the wizard (gesture-vocabulary.md: "on entry"). Reapplied
 * ranges always reset the pipeline — hysteresis and latch state built under
 * the old ranges must not survive into the new ones.
 */
function startCalibration(cam: CameraSource, auto: boolean): void {
  const apply = (r: ReturnType<typeof parseCalibration>): void => {
    if (!r) return;
    cam.setRanges(r);
    pipeline.reset();
  };
  if (auto) {
    const stored = parseCalibration(loadStoredCalibration());
    if (stored) {
      apply(stored);
      // Deferred: boot's scene mount writes the scene tip into the status line
      // right after this, which silently ate the message on the restore path.
      window.setTimeout(() => setStatus('calibration restored — Recalibrate to redo.'), 800);
      return;
    }
  }
  document.body.append(openCalibration({
    latestHands: () => cam.latestHands(),
    applyRanges: (r) => { cam.setRanges(r); pipeline.reset(); },
    done: (status) => setStatus(status),
  }));
}

/* --------------------------- the start gate --------------------------- */

/**
 * Both buttons do their work inside the tap. That is not tidiness: Safari
 * requires the AudioContext AND video.play() to begin inside a user gesture,
 * and doing either one later fails in ways that look like a permission denial.
 */
function gate(): HTMLElement {
  const withCamera = button('Start with camera', async () => {
    withCamera.disabled = true;
    padOnly.disabled = true;
    withCamera.textContent = 'Starting…';
    holdScreenAwake();
    // Offer the escape hatch after six seconds, but do NOT touch `note` — the
    // camera reports real progress through it now ("Downloading the hand model
    // — 3.2 / 7.5 MB"), and overwriting that with a generic line would hide the
    // one thing that tells you whether anything is actually happening.
    const slow = window.setTimeout(() => {
      hint.textContent = '~17 MB on first run, cached afterwards. Wi-Fi is much kinder than cellular. '
        + 'You can start on the pad instead if you would rather not wait.';
      hint.hidden = false;
      padOnly.disabled = false;
    }, 6000);
    try {
      await startAudio();
      gateNote = note;
      const cam = createCameraSource({ onStatus: cameraStatus });
      await cam.start(); // getUserMedia + play() + model load, all inside this tap
      camera = cam;
      window.clearTimeout(slow);
      overlay.remove();
      boot();
    } catch (err) {
      window.clearTimeout(slow);
      withCamera.disabled = false;
      padOnly.disabled = false;
      withCamera.textContent = 'Start with camera';
      note.textContent = cameraError(err as Error);
      note.classList.add('warn');
    }
  }, 'primary big');

  const padOnly = button('Use the pad instead', async () => {
    try {
      holdScreenAwake();
      await startAudio();
      overlay.remove();
      boot();
    } catch (err) {
      note.textContent = `Could not start audio: ${(err as Error).message}`;
      note.classList.add('warn');
    }
  }, 'ghost');

  const note = el('p', { class: 'note' }, [
    'Browsers only allow sound and camera to begin inside a tap. Nothing is uploaded — tracking runs on the device.',
  ]);
  /** Shown only if startup is slow, so it never competes with live progress. */
  const hint = el('p', { class: 'note dim', hidden: 'true' });
  const overlay = el('div', { class: 'gate', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Start' }, [
    el('div', { class: 'gate-card' }, [
      el('div', { class: 'gate-mark', html: markSvg(56) }),
      el('h1', {}, ['Gesture Synth']),
      el('p', { class: 'lede' }, [
        'Five instrument scenes carried over from the Chirp tools, played with your hands.',
      ]),
      withCamera,
      padOnly,
      note,
      hint,
      el('p', { class: 'note dim' }, [
        'The pad plays the same instruments with a pointer, and needs no camera, permission or model download.',
      ]),
    ]),
  ]);
  return overlay;
}

/**
 * Keep the screen awake. You play this by NOT touching the screen, so the phone
 * reads a performance as idle and auto-locks — typically at 30 seconds, which
 * kills the camera and the audio mid-take.
 *
 * Fire-and-forget, never awaited inside the start tap: the gate's whole design
 * is that the AudioContext and video.play() happen inside that gesture, and an
 * awaited hop at the front of that chain risks spending Safari's transient
 * activation for nothing. Wake Lock needs a visible document, not activation.
 */
let wakeLock: WakeLockSentinel | null = null;
function holdScreenAwake(): void {
  if (document.visibilityState !== 'visible' || wakeLock) return;
  const wl = (navigator as Navigator & {
    wakeLock?: { request(t: 'screen'): Promise<WakeLockSentinel> };
  }).wakeLock;
  if (!wl) return;
  void wl.request('screen').then((s) => {
    wakeLock = s;
    s.addEventListener('release', () => { wakeLock = null; });
  }).catch(() => { /* unsupported or refused — the phone just locks as usual */ });
}
// The lock is dropped whenever the page hides; re-take it on return.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') holdScreenAwake();
});

/** Safari's bare NotAllowedError over HTTP reads like a denial. Say what it is. */
function cameraError(err: Error): string {
  if (err.name === 'NotAllowedError') {
    return location.protocol === 'https:' || location.hostname === 'localhost'
      ? 'Camera denied. Allow it in the browser’s site settings, or use the pad.'
      : 'Camera blocked because this page is not on HTTPS — Safari does not even offer the prompt over plain HTTP. Use the pad, or serve this over HTTPS.';
  }
  if (err.name === 'NotFoundError') return 'No camera found on this device. Use the pad.';
  // Timeouts are the interesting failure: the first deploy hung here forever
  // with no message at all, so name the stage that gave up rather than dumping
  // a bare exception.
  if (/timed out/i.test(err.message)) {
    return `${err.message}. This is usually a slow connection or a device the GPU tracker will not start on — `
      + 'try again on Wi-Fi, or use the pad, which needs no download.';
  }
  return `Could not start the camera: ${err.message}. Use the pad.`;
}

/* ------------------------------ the shell ----------------------------- */

/**
 * Guards the async mount against a second selection arriving mid-await.
 * Without it, tabbing through scenes quickly unmounts a HALF-mounted scene —
 * whose engine reference does not exist yet — and the engine that finishes
 * constructing afterwards is connected to the bus with nothing left to tear
 * it down. The loser's mount still completes, then unmounts itself.
 */
let mountGen = 0;
async function selectScene(id: SceneId): Promise<void> {
  if (!transport) return;
  const gen = ++mountGen;
  scene?.unmount();
  scene = null;
  transport.stopAll();
  pipeline.reset(); // no held pinch or latch crosses a scene boundary
  stage.replaceChildren();
  sceneId = id;
  const next = SCENE_FACTORIES[id]();
  pipeline.setSelectionSteps(next.selectionSteps ?? 7);
  pad.setMode(next.padMode);
  for (const b of tabsEl.children) {
    const on = (b as HTMLElement).dataset.scene === id;
    b.setAttribute('aria-selected', String(on));
    b.setAttribute('tabindex', on ? '0' : '-1');
  }
  // Each mount gets its own holder rather than the shared stage: a losing
  // mount resolves after the winner has already repopulated the stage, and
  // would otherwise append its panel next to the winner's.
  const holder = el('div', {}, [el('p', { class: 'blurb' }, [next.blurb])]);
  stage.append(holder);
  await next.mount(holder, {
    bus: getBus(), transport, setStatus,
    setSelectionSteps: (n) => pipeline.setSelectionSteps(n),
  });
  if (gen !== mountGen) { next.unmount(); holder.remove(); return; }
  scene = next;
}

function buildTabs(): void {
  for (const id of SCENE_ORDER) {
    const b = el('button', {
      class: 'tab', type: 'button', role: 'tab', 'data-scene': id,
      'aria-selected': String(id === sceneId), tabindex: id === sceneId ? '0' : '-1',
    }, [SCENE_LABELS[id]]);
    b.addEventListener('click', () => { void selectScene(id); });
    tabsEl.append(b);
  }
}

function meterLoop(): void {
  const bus = getBus();
  const data = new Uint8Array(bus.analyser.fftSize);
  const tick = (): void => {
    bus.analyser.getByteTimeDomainData(data);
    let peak = 0;
    for (let i = 0; i < data.length; i++) peak = Math.max(peak, Math.abs(data[i] - 128) / 128);
    meterBar.style.width = `${Math.min(100, peak * 140)}%`;
    meterBar.classList.toggle('hot', peak > 0.92);
    // The ladder claims "this is what your HAND selects" — only true while
    // the camera drives. Under pad input the mirror stays live but unadorned.
    preview?.setSelection(activeInput === 'camera' ? (scene?.selection?.() ?? null) : null);
    if (scene) {
      readoutEl.replaceChildren(...scene.readouts().flatMap(([k, v]) => [
        el('dt', {}, [k]), el('dd', {}, [v]),
      ]));
    }
    requestAnimationFrame(tick);
  };
  tick();
}

/**
 * The one-tap escape hatch for iOS: share() needs a live user gesture, and the
 * take does not exist until after the one that stopped the recording.
 */
function showShareTake(take: Take): void {
  shareSlot.replaceChildren(
    button('Share take', async () => {
      const how = await shareTakeNow(take);
      if (how !== 'cancelled') shareSlot.replaceChildren();
      setStatus(`${take.filename} — ${how}.`);
    }, 'primary small'),
    button('Download', () => {
      downloadTake(take);
      shareSlot.replaceChildren();
      setStatus(`${take.filename} — downloaded.`);
    }, 'ghost small'),
  );
}

function boot(): void {
  const bus = getBus();
  transport = createTransport(bus.ctx);

  // A take carries the composited preview when the camera is live, so the video
  // shows the hands that played it. With the pad it stays audio-only.
  recorder = createRecorder(bus.ctx, bus.tap, () => sceneId, () => preview?.captureTracks() ?? []);

  const recBtn = button('Record', async () => {
    if (!recorder?.supported) { setStatus('This browser has no MediaRecorder — recording is unavailable.'); return; }
    if (recorder.recording) {
      recBtn.textContent = 'Record';
      recBtn.classList.remove('rec');
      recBtn.disabled = true; // a second tap mid-stop would race the take
      const take = await recorder.stop();
      recBtn.disabled = false;
      // The ControlState stream rides beside the audio: same take, one .jsonl.
      // This is the file that lets a take be re-rendered under a different
      // cutoff mapping (Gate 8's A/B method) or converted later, without
      // re-performing it.
      const log = controlLog.stop();
      if (log && take) {
        downloadTake({
          blob: log, mime: 'application/jsonl', hasVideo: false, seconds: take.seconds,
          filename: take.filename.replace(/\.[^.]+$/, '') + '.control.jsonl',
        });
      }
      if (take) {
        const what = take.hasVideo ? 'video + audio' : 'audio';
        const size = take.blob.size >= 1048576
          ? `${(take.blob.size / 1048576).toFixed(1)} MB`
          : `${Math.round(take.blob.size / 1024)} KB`;
        const how = await deliverTake(take);
        if (how === 'needs-gesture') {
          // Safari will not open the share sheet from here: the take only
          // exists after MediaRecorder's stop event, by which point the tap
          // that started this is spent. Offer a fresh one.
          setStatus(`${take.filename} — ${take.seconds.toFixed(1)}s of ${what}, ${size}. Tap Share take to save it.`);
          showShareTake(take);
        } else {
          setStatus(`${take.filename} — ${take.seconds.toFixed(1)}s of ${what}, ${size}, ${how}.`);
        }
      }
    } else {
      recorder.start();
      controlLog.start(sceneId);
      recBtn.textContent = 'Stop';
      recBtn.classList.add('rec');
      setStatus(camera
        ? 'Recording the preview and the master bus in real time, plus the control stream.'
        : 'Recording the master bus in real time — what you hear is what you get.');
    }
  }, 'ghost rec-btn');

  const header = el('header', { class: 'top' }, [
    el('div', { class: 'brand' }, [
      el('span', { class: 'brandmark', html: markSvg(26) }),
      el('span', { class: 'brandname' }, ['Gesture Synth']),
    ]),
    el('div', { class: 'top-right' }, [
      el('div', { class: 'level', 'aria-hidden': 'true' }, [meterBar]),
      recBtn,
    ]),
  ]);

  buildTabs();

  // The preview is a STICKY BAR above <main>, not a panel inside the aside.
  //
  // On a phone `.main` is one column and the aside sits below the whole scene
  // panel, so the preview would be off-screen while you play — unusable for an
  // instrument you aim with your hands, and worse than cosmetic: WebKit pauses
  // an off-screen muted video, `currentTime` then stops advancing, and the
  // frame gate in camera.ts sees a repeated timestamp and returns forever.
  // Keeping it on-screen removes that exposure rather than reacting to it.
  //
  // The recorder reads preview.captureTracks() off the canvas, so where the
  // canvas is mounted does not affect a take.
  const parts: HTMLElement[] = [header, tabsEl, statusEl, latchEl, shareSlot];
  if (camera) {
    preview = createPreview();
    preview.attach(camera.video);
    preview.setNote(`tracking on ${camera.delegate}`);
    preview.start();
    parts.push(el('div', { class: 'preview-bar' }, [preview.root]));
  }

  // The two open experiments, live-toggleable so Gates 4 and 8 are decidable
  // on a device mid-session — and recorded into the control log when they
  // change, so a take knows which arm produced it.
  const trigBtn = button(`Trigger: ${getExperiments().trigger === 'quantized' ? 'grid' : 'free'}`, () => {
    const next = getExperiments().trigger === 'quantized' ? 'immediate' : 'quantized';
    setExperiments({ trigger: next });
    trigBtn.textContent = `Trigger: ${next === 'quantized' ? 'grid' : 'free'}`;
    setStatus(next === 'quantized'
      ? 'Notes land on the grid — the gesture arms, the grid decides when.'
      : 'Notes fire the moment the gesture does.');
  }, 'ghost small');
  const cutBtn = button(`Cutoff: ${getExperiments().cutoffSource}`, () => {
    const next = getExperiments().cutoffSource === 'spread' ? 'aperture' : 'spread';
    setExperiments({ cutoffSource: next });
    cutBtn.textContent = `Cutoff: ${next}`;
    setStatus(next === 'spread'
      ? 'Cutoff on finger spread — decoupled from the trigger.'
      : 'Cutoff on the pinch — closing toward the trigger darkens the note.');
  }, 'ghost small');

  // The input picker is a FIRST-CLASS control, not a footer easter egg. The
  // previous toggle lived in the footer — below the fold on a phone — while
  // the pad sat on screen fully rendered and dead whenever the camera drove.
  // Dragging a dead pad with no explanation is how "the pad isn't pickable"
  // reads to a player. Now: a Camera/Pad radio directly above the pad, and an
  // overlay ON the dead pad that names the situation and switches on tap.
  const sideKids: HTMLElement[] = [];
  if (camera) {
    const mk = (id: 'camera' | 'pad', label: string): HTMLButtonElement => {
      const b = el('button', {
        class: 'chip', type: 'button', role: 'radio',
        'aria-checked': String(id === 'camera'),
      }, [label]) as HTMLButtonElement;
      b.addEventListener('click', () => { void chooseInput(id); });
      return b;
    };
    inputPick = { camera: mk('camera', 'Camera'), pad: mk('pad', 'Pad') };
    sideKids.push(el('div', { class: 'field' }, [
      el('span', { class: 'slabel' }, ['Input']),
      el('div', { class: 'chips', role: 'radiogroup', 'aria-label': 'Input' }, [
        inputPick.camera, inputPick.pad,
        // Ranges drift as the arm tires within a session (the vocab doc's own
        // caveat) — recalibrating must not require a reload.
        button('Recalibrate', () => { if (camera) startCalibration(camera, false); }, 'ghost small'),
      ]),
    ]));

    padOverlay = el('button', { class: 'pad-overlay', type: 'button' }, [
      'camera is driving — tap to play by touch',
    ]);
    padOverlay.addEventListener('click', () => { void chooseInput('pad'); });
    pad.root.append(padOverlay);
  }
  sideKids.push(pad.root, readoutEl);

  app.replaceChildren(
    ...parts,
    el('main', { class: 'main' }, [stage, el('aside', { class: 'side' }, sideKids)]),
    el('footer', { class: 'foot' }, [
      el('span', {}, [`output latency ~${outputLatencyMs(bus.ctx)} ms`]),
      trigBtn,
      cutBtn,
      ...(camera ? [] : [el('span', {}, ['pad input — no camera this session'])]),
      // Which build is this? Answering that from the page itself is the whole
      // point — a stale deployment and a cached page are indistinguishable
      // otherwise, and both have wasted a test session already.
      el('span', { class: 'build-id', title: `built ${__BUILD_TIME__}` }, [`build ${__BUILD_ID__}`]),
    ]),
  );

  // EXACTLY ONE source drives the scene. Both speak the same contract, but
  // running both was the worst bug in the first camera build: the pad's rAF
  // loop emits its idle values on every frame whether or not it was touched,
  // so the scene was updated twice per frame — the pad overwrote whatever the
  // camera had just set, and the alternating apertures (camera pinched 0.2,
  // pad idle 0.8) reset the pinch hysteresis every other frame, machine-gunning
  // one held pinch into ~30 triggers a second. "Whichever moved last wins" is
  // not a mechanism that falls out of two live sources; it would need explicit
  // arbitration. One active source is the honest version.
  //
  // Every frame runs through the ONE control pipeline; the scene gets the
  // frame and the resulting ControlState. The latch indicator lives here
  // because the dropout contract requires the UI to show a latched state —
  // a performer must see that the pitch hand went missing, not hear an
  // unexplained frozen note.
  const route = (frame: FeatureFrame): void => {
    const control = pipeline.update(frame);
    latchEl.hidden = !control.latched;
    if (control.panic) {
      // Kill everything, bluntly: stop the transport, duck the bus so ringing
      // tails die under it, and re-mount the scene so every Run toggle and
      // held state starts over. Panic is a safety net for a stuck voice, not
      // a performance move — correctness over elegance.
      transport?.stopAll();
      bus.duck();
      setStatus('panic — all voices killed');
      void selectScene(sceneId);
      return;
    }
    scene?.update(frame, control);
    controlLog.push(frame, control);
  };

  pad.onFrame(route);
  pad.setMode('xy');

  if (camera) {
    camera.onFrame((frame) => {
      preview?.setHands(camera!.latestHands());
      route(frame);
    });
    activeInput = 'camera';
  } else {
    activeInput = 'pad';
    void pad.start();
  }
  reflectInput();

  // Calibration on entry: restore or run the wizard. After the shell exists,
  // so the wizard overlays a playable app and Skip lands somewhere real.
  if (camera) startCalibration(camera, true);

  meterLoop();
  void selectScene(sceneId);
}

/**
 * Switch the driving input. Stop-then-start rather than callback gating, so
 * the inactive source does no work at all; re-mount the scene in between,
 * because every adapter's hysteresis/latch memory was built from the old
 * source's value ranges and must not carry across.
 */
let inputBusy = false;
async function setInput(next: 'camera' | 'pad'): Promise<void> {
  if (!camera || inputBusy) return;
  inputBusy = true;
  try {
    // selectScene resets the pipeline and every adapter — nothing held under
    // one source's value ranges survives into the other's.
    if (next === 'pad') {
      camera.stop();
      preview?.setNote('not tracking — pad is driving');
      // A frozen skeleton and a stale ladder over live video would claim the
      // camera is still tracking. Clear both; the video freezing is honest.
      preview?.setHands([]);
      preview?.setSelection(null);
      await selectScene(sceneId);
      await pad.start();
    } else {
      pad.stop();
      await selectScene(sceneId);
      await camera.start();
      preview?.setNote(`tracking on ${camera.delegate}`);
    }
    activeInput = next;
    setStatus(next === 'pad' ? 'Pad input — drag to play.' : 'Camera input.');
  } finally {
    inputBusy = false;
  }
}

const link = document.createElement('link');
link.rel = 'icon';
link.href = faviconDataUrl();
document.head.append(link);

if (!isStarted()) app.append(gate());
